import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  Box,
  Button,
  Tabs,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  ListSubheader,
  Typography,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import UnitTesting from "./projectDetails/UnitTesting";
import MemoryTesting from "./projectDetails/MemoryLeak";
import StyledTab from "./common/StyledTab";

const TestingProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const token = localStorage.getItem("access_token");
  const organizationId = localStorage.getItem("organization_id");
  const newRunId = useRef(uuidv4());

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const defaultTab =
    typeof location.state?.activeTab === "number"
      ? location.state.activeTab
      : 2;
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (!organizationId) return;

    const fetchAll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects?organization_id=${organizationId}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        setProjects(data.projects_list || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
      }
    };

    fetchAll();
  }, [organizationId, token]);

  const fetchProjectDetails = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setProject(data);
    } catch (err) {
      console.error("Error fetching project details:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const handleTabChange = (_, newVal) => {
    setActiveTab(newVal);
  };

  const handleProjectChange = (e) => {
    const selectedProjId = e.target.value;
    const selectedProj =
      projects.find((p) => p.project_id === selectedProjId) || {};
    setActiveTab(2);

    navigate(`/projects/testing/${selectedProjId}`, {
      state: {
        activeTab: 2,
        projectName: selectedProj.name,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor:
          theme.palette.mode === "dark" ? "#282828" : "background.default",
        color: "text.primary",
        p: 0,
        m: 0,
      }}
    >
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              rowGap: 2,
              mb: 3,
            }}
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select Project</InputLabel>
              <Select
                value={projectId || ""}
                label="Select Project"
                onChange={handleProjectChange}
                size="small"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                renderValue={(sel) => {
                  const p = projects.find((x) => x.project_id === sel);
                  return (
                    p?.name || location.state?.projectName || "Select Project"
                  );
                }}
                sx={{
                  maxWidth: 300,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <ListSubheader disableSticky>
                  <TextField
                    autoFocus
                    placeholder="Search projects..."
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>

                {filteredProjects.map((p) => (
                  <MenuItem
                    key={p.project_id}
                    value={p.project_id}
                    title={p.name}
                    sx={{
                      maxWidth: 280,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 2 }}>
                {project?.name || "No project selected"}
              </Typography>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 3,
                  "& .MuiTabs-indicator": {
                    height: "3px",
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                <StyledTab label="Unit Testing" value={2} />
                <StyledTab label="Memory Testing" value={4} />
              </Tabs>

              {activeTab === 2 && <UnitTesting />}
              {activeTab === 4 && <MemoryTesting />}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default TestingProjectDetails;